<template>
  <div
    class="bg-white rounded-lg border border-gray-200 p-4 relative cursor-pointer hover:shadow-md transition-shadow"
    @click="$emit('click', item)"
  >
    <!-- Top row: Frequency badge + green tick + count badge + menu -->
    <div class="flex items-start justify-between gap-2 mb-3">
      <div class="flex items-center gap-2 flex-wrap min-w-0">
        <span
          class="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full shrink-0"
          :class="frequencyBadgeClass"
        >
          {{ formatFrequencyLabel(item.frequency) }}
          <span
            v-if="item.resultsChanged"
            v-html="CHECK_ICON"
            class="w-3.5 h-3.5 text-green-600 shrink-0"
            :title="UPDATE_TOOLTIP"
          ></span>
        </span>
        <span
          v-if="item.initialResultCount != null"
          class="inline-flex items-center justify-center min-w-[1.5rem] px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
          :class="frequencyBadgeClass"
        >
          {{ item.initialResultCount }}
        </span>
      </div>
      <div ref="menuContainerRef" class="shrink-0" @click.stop>
        <button
          class="text-gray-400 hover:text-gray-600 p-1"
          @click="$emit('toggleMenu', item.id)"
        >
          <span v-html="MORE_ICON"></span>
        </button>
        <div
          v-if="openMenuId === item.id"
          class="absolute right-0 top-8 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20"
        >
          <button
            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="handleViewDetails"
          >
            View Details
          </button>
          <button
            class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            @click="handleRemove"
          >
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Query Text -->
    <p class="text-sm text-gray-900 mb-2 pr-8">{{ item.query }}</p>

    <!-- Added Date -->
    <span class="text-xs text-gray-400">
      Added on {{ formatWatchlistDate(item.addedAt) }}
    </span>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onUnmounted } from "vue";

const CHECK_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>`;
const MORE_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.5"/><circle cx="12" cy="12" r="1.5"/><circle cx="12" cy="19" r="1.5"/></svg>`;

const UPDATE_TOOLTIP = "results changed in last update";
const frequencyBadgeClass =
  "bg-purple-100 text-purple-700 border border-purple-200";

export interface WatchlistItem {
  id: string;
  query: string;
  programId?: string | null;
  frequency: string;
  addedAt: string;
  initialResultCount: number;
  previousResultCount?: number | null;
  currentResultCount: number;
  resultsChanged: boolean;
  lastRunAt?: string | null;
}

export default defineComponent({
  name: "WatchlistCard",
  props: {
    item: {
      type: Object as () => WatchlistItem,
      required: true,
    },
    openMenuId: {
      type: String as () => string | null,
      default: null,
    },
  },
  emits: ["click", "toggleMenu", "viewDetails", "remove"],
  setup(props, { emit }) {
    const menuContainerRef = ref<HTMLElement | null>(null);

    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuContainerRef.value &&
        !menuContainerRef.value.contains(e.target as Node)
      ) {
        emit("toggleMenu", props.item.id);
      }
    };

    watch(
      () => props.openMenuId === props.item.id,
      (isOpen) => {
        if (isOpen) {
          setTimeout(
            () => document.addEventListener("click", handleClickOutside),
            0
          );
        } else {
          document.removeEventListener("click", handleClickOutside);
        }
      }
    );

    onUnmounted(() => {
      document.removeEventListener("click", handleClickOutside);
    });

    const formatFrequencyLabel = (f: string) => {
      const map: Record<string, string> = {
        daily: "Daily Updates",
        weekly: "Weekly Updates",
        monthly: "Monthly Updates",
      };
      return map[f] || f;
    };

    const formatWatchlistDate = (dateStr: string) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    const handleViewDetails = () => {
      emit("viewDetails", props.item);
    };

    const handleRemove = () => {
      emit("remove", props.item.id);
    };

    return {
      menuContainerRef,
      CHECK_ICON,
      MORE_ICON,
      UPDATE_TOOLTIP,
      frequencyBadgeClass,
      formatFrequencyLabel,
      formatWatchlistDate,
      handleViewDetails,
      handleRemove,
    };
  },
});
</script>
